import React, { useEffect, useRef, useState } from "react";
import "./ITixfeatures.css";

/* ============================================================
   IMAGES – apni image ke paths yahan badlo (public/iTixFiles_img/ me rakho)
   ============================================================ */
const IMAGES = {
  personal: "ImagesNew/ifile_feat1.webp",
  organization: "/ImagesNew/electronicfile.jpeg",
  capture: "/ImagesNew/smartdocument.jpeg",
  archive: "/ImagesNew/digitization.jpeg",
  workflow: "ImagesNew/ifile_feat4.webp",
  ticketing: "/ImagesNew/ticketing.jpeg",
  insights: "/ImagesNew/performance.jpeg",
  security: "/ImagesNew/accesscontrol.jpeg",
};

/* ============================================================
   CONTENT  (client ka content – yahin se edit karo)
   ============================================================ */
const HEAD = {
  badge: "CORE FEATURES",
  title: "NEXT GEN IFILE EXPERIENCE",
  subtitle:
    "Smart automation, centralized control, and secure file management for modern digital workflows",
};

const FEATURES = [
  {
    tag: "PERSONAL FILES",
    image: IMAGES.personal,
    title: "Personal Digital File Management",
    tagline: "Your important documents, organized and ready when you need them.",
    lead: "Create your own digital document space and organize files into folders such as Education, Identity, Property, Career and Finance.",
    bullets: [
      "Store documents in customized folders and subfolders.",
      "Keep previous document versions while accessing the latest version easily.",
      "Reuse existing documents when preparing new applications.",
      "Track which applications use a particular document.",
      "Keep application status and related documents connected.",
      "Search and retrieve documents without searching through piles of paper.",
    ],
    example:
      "Keep your identity proof, address proof and educational certificates organized. When applying for a service, select the required documents instead of repeatedly searching for and uploading them.",
  },
  {
    tag: "ORGANIZATIONS",
    image: IMAGES.organization,
    title: "Electronic File Management for Organizations",
    tagline: "Move from paper files and disconnected processes to organized digital workflows.",
    lead: "iTixFiles helps offices, institutions, businesses and service organizations manage people, records, applications, documents, approvals and related work.",
    bullets: [
      "Maintain digital files for citizens, customers, students, employees, members and vendors.",
      "Create customized folders, document categories and application types.",
      "Upload, scan, organize and reuse documents across applications.",
      "Assign applications to departments, teams or responsible employees.",
      "Track application progress, pending actions, deadlines and approvals.",
      "Maintain a chronological activity history for accountability and performance monitoring.",
    ],
    example:
      "A citizen applies for a new electricity connection. The office collects the required documents, verifies them, assigns inspection tasks, obtains approval and tracks the application until completion.",
  },
  {
    tag: "SMART CAPTURE",
    image: IMAGES.capture,
    title: "Smart Document Capture & Paperless Applications",
    tagline: "Turn scanned documents into useful digital information.",
    lead: "Reduce repetitive typing by using document scanning, text extraction and voice input in supported workflows.",
    bullets: [
      "Upload scanned documents, images and PDFs.",
      "Extract searchable text and relevant information using OCR.",
      "Identify fields such as names, dates, amounts and document numbers where supported.",
      "Review and correct extracted information before saving.",
      "Use captured information to prefill application forms where configured.",
      "Use voice-to-text for longer descriptions, including supported regional languages such as Marathi, subject to device and browser capabilities.",
    ],
    example:
      "Scan an applicant's supporting documents, review the extracted details, prefill the application form and submit it for processing.",
  },
  {
    tag: "ARCHIVES",
    image: IMAGES.archive,
    title: "Digitization of Old & Archived Records",
    tagline: "Bring historical paper records into your searchable digital repository.",
    lead: "Old files do not need to remain difficult-to-access paper archives.",
    bullets: [
      "Scan existing paper documents and upload scanned PDFs or images.",
      "Extract text and available metadata using OCR.",
      "Organize records under the correct person, category, folder or case after verification.",
      "Search extracted text and document metadata to find records faster.",
      "Preserve original documents and maintain version history.",
      "Link historical records to relevant people or applications where appropriate.",
    ],
    example:
      "An office scans years of archived electricity connection applications. Staff can retrieve records using available applicant details, document information or extracted text instead of manually searching through physical files.",
  },
  {
    tag: "WORKFLOW",
    image: IMAGES.workflow,
    title: "Application & Workflow Tracking",
    tagline: "Know where every application stands and who needs to act next.",
    lead: "Create configurable workflows for different services, departments and organizational requirements.",
    bullets: [
      "Define application types, stages and required documents.",
      "Assign responsibility to individuals or departments.",
      "Manage document verification, inspections, reviews and approvals.",
      "Track pending, overdue, completed and rejected applications.",
      "Maintain application timelines and activity histories.",
      "Connect application stages with tickets and tasks.",
    ],
    exampleLabel: "Example workflow",
    steps: ["Application Submitted", "Document Verification", "Inspection", "Approval", "Completed"],
  },
  {
    tag: "TICKETING",
    image: IMAGES.ticketing,
    title: "Ticketing & Task Management",
    tagline: "Keep daily operational work organized, assigned and accountable.",
    lead: "Use the ticketing system to manage service requests, complaints, internal tasks and operational issues.",
    bullets: [
      "Create and assign tickets to individuals or teams.",
      "Set priorities, deadlines and statuses.",
      "Add comments, attachments and linked tickets.",
      "Connect tickets to relevant people, documents and applications.",
      "Monitor pending work, overdue items and completed tasks.",
      "Review activity history to understand progress and responsibility.",
    ],
    example:
      "An electricity connection application requires a site inspection. iTixFiles creates or links an inspection ticket, assigns it to the responsible team and tracks the task through completion.",
  },
  {
    tag: "INSIGHTS",
    image: IMAGES.insights,
    title: "Performance Insights & Accountability",
    tagline: "Turn activity records into operational visibility.",
    lead: "Help managers understand how work moves through the organization.",
    bullets: [
      "Monitor open, pending and overdue work.",
      "Track application completion rates.",
      "Identify long-pending cases and workflow bottlenecks.",
      "Review assignment, verification and approval histories.",
      "Analyze created-versus-completed work over selected periods.",
    ],
  },
  {
    tag: "SECURITY",
    image: IMAGES.security,
    title: "Access Control & Secure Document Handling",
    tagline: "Keep information organized and accessible to the right people.",
    bullets: [
      "Separate organizational workspaces and records.",
      "Manage role-based access to documents, applications and tickets.",
      "Control access to document downloads and sensitive information.",
      "Preserve document versions and activity histories.",
      "Support accountable handling of records across teams.",
    ],
  },
];

/* ============================================================
   scroll-in hook
   ============================================================ */
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (!("IntersectionObserver" in window)) {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen];
};

/* ============================================================
   ROW
   ============================================================ */
const Row = ({ f, i }) => {
  const [ref, seen] = useInView();
  return (
    <article
      ref={ref}
      className={`nf__row ${i % 2 ? "nf__row--flip" : ""} ${seen ? "nf__row--in" : ""}`}
    >
      <div className="nf__frame">
        <div className="nf__frame-inner">
          <span className="nf__tag">{f.tag}</span>
          <img src={f.image} alt={f.title} loading="lazy" className="nf__img" />
        </div>
      </div>

      <div className="nf__text">
        <span className="nf__num" aria-hidden="true">
          {String(i + 1).padStart(2, "0")}
        </span>
        <h3 className="nf__h3">{f.title}</h3>
        <span className="nf__line" />
        <p className="nf__tagline">{f.tagline}</p>
        {f.lead && <p className="nf__lead">{f.lead}</p>}

        <ul className="nf__list">
          {f.bullets.map((b, k) => (
            <li key={b} style={{ "--k": k }}>
              {b}
            </li>
          ))}
        </ul>

        {f.example && (
          <div className="nf__example">
            <strong>Example</strong>
            <p>{f.example.replace(/^Example:\s*/, "")}</p>
          </div>
        )}

        {f.steps && (
          <div className="nf__example">
            <strong>{f.exampleLabel}</strong>
            <ol className="nf__steps">
              {f.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </article>
  );
};

/* ============================================================
   SECTION
   ============================================================ */
const ITixFeatures = () => (
  <section className="nf" id="core-features">
    <div className="nf__container">
      <header className="nf__head">
        <span className="nf__pill">{HEAD.badge}</span>
        <h2 className="nf__title">{HEAD.title}</h2>
        <p className="nf__sub">{HEAD.subtitle}</p>
      </header>

      <div className="nf__rows">
        {FEATURES.map((f, i) => (
          <Row key={f.title} f={f} i={i} />
        ))}
      </div>
    </div>
  </section>
);

export default ITixFeatures;